import React, { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { selectSelectedOutletId } from '@store/outlets/outletSelectors';
import {
  fetchCompanyUsersThunk,
  selectAllManagedUsers,
  selectUsersLoading,
} from '@store/users';
import {
  Calendar,
  Mail,
  Store,
  KeyRound,
  Shield,
  Utensils,
  Wallet,
  Building,
  Crown,
  Lock,
  Calculator,
} from 'lucide-react';
import { LoadingState } from '@components/common';
import { ManagedUser } from '../../../types/userManagement';

// Define the 6 specific roles to show (matching the app's primary green theme)
const ROLE_DETAILS = {
  super: {
    label: 'Super Admin',
    icon: Crown,
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
  },
  manager: {
    label: 'Manager',
    icon: Lock,
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
  },
  waiter: {
    label: 'Waiter',
    icon: Utensils,
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
  },
  cashier: {
    label: 'Cashier',
    icon: Wallet,
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
  },
  receptionist: {
    label: 'Receptionist',
    icon: Building,
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
  },
  accountant: {
    label: 'Accountant',
    icon: Calculator,
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
  },
};

type FilteredRole = keyof typeof ROLE_DETAILS;

interface UserCardProps {
  user: ManagedUser;
}

const UserCard: React.FC<UserCardProps> = ({ user }) => {
  const initials = useMemo(() => {
    return `${user.first_name[0] || ''}${user.last_name[0] || ''}`.toUpperCase();
  }, [user.first_name, user.last_name]);

  const roleConfig = ROLE_DETAILS[user.role as FilteredRole] || {
    label: user.role,
    icon: Shield,
    bg: 'bg-slate-50',
    text: 'text-slate-700',
    ring: 'ring-slate-200',
  };

  const Icon = roleConfig.icon;

  const joinDate = useMemo(() => {
    try {
      return new Date(user.created_at).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return '-';
    }
  }, [user.created_at]);

  // Unified color theme for avatars based on active status
  const avatarBgClass = user.is_active ? 'bg-emerald-700' : 'bg-slate-400';
  const statusBadgeClass = user.is_active ? 'bg-emerald-500' : 'bg-red-400';
  const roleBadgeBg = user.is_active ? roleConfig.bg : 'bg-slate-50';
  const roleBadgeText = user.is_active ? roleConfig.text : 'text-slate-500';
  const roleBadgeRing = user.is_active ? roleConfig.ring : 'ring-slate-100';

  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-100 bg-white p-6 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col items-center text-center group">
      {/* Active/Inactive dot status indicator */}
      <span
        className={`absolute top-4 right-4 h-2.5 w-2.5 rounded-full ${statusBadgeClass}`}
        title={user.is_active ? 'Active Account' : 'Disabled Account'}
      />

      {/* Circle Initials Avatar (unified green/slate style) */}
      <div
        className={`relative h-16 w-16 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-inner mb-4 transition-transform duration-300 group-hover:scale-105 ${avatarBgClass}`}
      >
        {initials}
        <span className="absolute bottom-0 right-0 rounded-full bg-white p-1 shadow-sm border border-slate-50">
          <Icon size={12} className={roleBadgeText} />
        </span>
      </div>

      {/* Name and Handle */}
      <h4 className="text-base font-bold text-slate-900 line-clamp-1">
        {user.first_name} {user.last_name}
      </h4>
      <p className="text-xs text-slate-400 font-mono mt-0.5">
        @{user.email.split('@')[0]}
      </p>

      {/* Role Badge */}
      <span
        className={`inline-flex items-center gap-1 mt-3 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${roleBadgeBg} ${roleBadgeText} ${roleBadgeRing}`}
      >
        <Icon size={10} />
        {roleConfig.label}
      </span>

      {/* Profile Metrics Details (unified slate fonts) */}
      <div className="w-full border-t border-slate-50 mt-6 pt-4 space-y-2.5 text-left text-xs">
        <div className="flex items-center justify-between text-slate-500">
          <span className="flex items-center gap-1.5">
            <Calendar size={13} className="text-slate-400" />
            <span>Join Date</span>
          </span>
          <span className="font-medium text-slate-900">{joinDate}</span>
        </div>

        <div className="flex items-center justify-between text-slate-500">
          <span className="flex items-center gap-1.5">
            <Mail size={13} className="text-slate-400" />
            <span>Email</span>
          </span>
          <span
            className="font-medium text-slate-900 truncate max-w-[140px]"
            title={user.email}
          >
            {user.email}
          </span>
        </div>

        <div className="flex items-center justify-between text-slate-500">
          <span className="flex items-center gap-1.5">
            <Store size={13} className="text-slate-400" />
            <span>Outlet</span>
          </span>
          <span
            className="font-medium text-slate-900 truncate max-w-[140px]"
            title={user.outlet_name || 'Unassigned'}
          >
            {user.outlet_name || 'Unassigned'}
          </span>
        </div>

        <div className="flex items-center justify-between text-slate-500">
          <span className="flex items-center gap-1.5">
            <KeyRound size={13} className="text-slate-400" />
            <span>Passcode Login</span>
          </span>
          <span
            className={`font-semibold ${
              user.has_passcode ? 'text-emerald-600' : 'text-slate-400'
            }`}
          >
            {user.has_passcode ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>
    </div>
  );
};

export const RolesPermissionsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const selectedOutletId = useAppSelector(selectSelectedOutletId);
  const users = useAppSelector(selectAllManagedUsers);
  const isUsersLoading = useAppSelector(selectUsersLoading);

  useEffect(() => {
    dispatch(fetchCompanyUsersThunk(selectedOutletId));
  }, [dispatch, selectedOutletId]);

  // Aggregate metric counts reactively from the loaded active users list
  const roleSummaries = useMemo(() => {
    return (Object.keys(ROLE_DETAILS) as FilteredRole[]).map((role) => {
      const count = users.filter(
        (u) => u.role === role && u.is_active === true,
      ).length;
      return {
        role,
        count,
        ...ROLE_DETAILS[role],
      };
    });
  }, [users]);

  // Group user cards by role, filtering for ONLY the 6 specified roles
  const usersGroupedByRole = useMemo(() => {
    const groups: Record<FilteredRole, ManagedUser[]> = {
      super: [],
      manager: [],
      waiter: [],
      cashier: [],
      receptionist: [],
      accountant: [],
    };

    users.forEach((user) => {
      const roleKey = user.role as FilteredRole;
      if (groups[roleKey]) {
        groups[roleKey].push(user);
      }
    });

    return groups;
  }, [users]);

  if (isUsersLoading && users.length === 0) {
    return <LoadingState message="Loading staff roles & metrics..." />;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] p-6 max-w-7xl mx-auto space-y-6 overflow-hidden">
      {/* Page Header */}
      <div className="shrink-0">
        <h1 className="text-2xl font-bold text-slate-900">
          Roles & Permissions
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Review employee counts, manage authorization categories, and view user
          details across selected outlets.
        </p>
      </div>

      {/* Metrics Cards Grid (unified green theme icons) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 shrink-0">
        {roleSummaries.map((summary) => {
          const SummaryIcon = summary.icon;
          return (
            <div
              key={summary.role}
              className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm flex flex-col items-center justify-center text-center transition-all duration-200 hover:-translate-y-0.5"
            >
              <div className={`p-2 rounded-xl mb-2 ${summary.bg}`}>
                <SummaryIcon size={20} className={summary.text} />
              </div>
              <span className="text-2xl font-extrabold text-slate-900">
                {summary.count}
              </span>
              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mt-1">
                {summary.label}s
              </span>
            </div>
          );
        })}
      </div>

      {/* Bounded Scroll Container to prevent page from growing beyond view height */}
      <div className="flex-1 overflow-y-auto pr-2 space-y-8 min-h-0 custom-scrollbar pb-6">
        {(Object.keys(ROLE_DETAILS) as FilteredRole[]).map((role) => {
          const groupUsers = usersGroupedByRole[role] || [];
          if (groupUsers.length === 0) return null;

          const config = ROLE_DETAILS[role];
          const GroupIcon = config.icon;

          return (
            <section key={role} className="space-y-4">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                <div className={`p-1.5 rounded-lg ${config.bg}`}>
                  <GroupIcon size={16} className={config.text} />
                </div>
                <h3 className="text-lg font-bold text-slate-900">
                  {config.label}s
                </h3>
                <span className="bg-slate-100 text-slate-600 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {groupUsers.length}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {groupUsers.map((user) => (
                  <UserCard key={user.id} user={user} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default RolesPermissionsPage;
